import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from 'antd';
import { WarningOutlined, InfoCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { businessCardAPI } from '../../services/api';

const { Text } = Typography;

type MessageLevel = 'warn' | 'info' | 'attention';

interface LevelStyle {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  hoverColor: string;
  icon: React.ReactNode;
}

/** レベルごとの表示スタイル。未知/未設定の divvalue は 'warn'（既存デザイン）にフォールバックする。 */
const LEVEL_STYLES: Record<MessageLevel, LevelStyle> = {
  warn: {
    backgroundColor: '#fff1f0',
    borderColor: '#ffa39e',
    textColor: '#cf1322',
    hoverColor: '#a8071a',
    icon: <WarningOutlined style={{ marginRight: '8px' }} />
  },
  info: {
    backgroundColor: '#e6f7ff',
    borderColor: '#91caff',
    textColor: '#0958d9',
    hoverColor: '#003eb3',
    icon: <InfoCircleOutlined style={{ marginRight: '8px' }} />
  },
  attention: {
    backgroundColor: '#fffbe6',
    borderColor: '#ffe58f',
    textColor: '#d48806',
    hoverColor: '#ad6800',
    icon: <WarningOutlined style={{ marginRight: '8px' }} />
  }
};

/** 閉じた状態を記憶する期間（この時間が経過するまでは再表示しない） */
const DISMISS_TTL_MS = 30 * 60 * 1000; // 30分
const DISMISS_STORAGE_KEY_PREFIX = 'maintenanceMessageDismissedAt:';

/** divvalue からレベルを判定する（info/attention 以外はすべて 'warn' 扱い。大文字小文字・前後空白は無視） */
const resolveLevel = (divvalue?: string): MessageLevel => {
  const normalized = divvalue?.trim().toLowerCase();
  if (normalized === 'info') return 'info';
  if (normalized === 'attention') return 'attention';
  return 'warn';
};

/**
 * メンテナンスメッセージ表示バナー
 *
 * 区分「UptoneMaintenanceMessage」をAPIから取得し、
 * 取得できた場合のみレベル（divvalue）に応じたデザインの警告バナーを表示。
 * 閉じた場合はメッセージ内容＋レベルをキーに閉じた時刻を記憶し、
 * 一定時間（DISMISS_TTL_MS）が経過するまでは再表示しない。
 *
 */
const MaintenanceMessageBanner: React.FC = () => {
  const [message, setMessage] = useState<string>('');
  const [level, setLevel] = useState<MessageLevel>('warn');
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [hoverClose, setHoverClose] = useState<boolean>(false);

  /** メンテナンスメッセージを取得してセットする（区分「UptoneMaintenanceMessage」） */
  const loadMaintenanceMessage = useCallback(async () => {
    try {
      const response = await businessCardAPI.getComSystemMapperValueList('UptoneMaintenanceMessage');
      if (response.statuscode === 200) {
        const data = response.comsystemdatalist;
        if (data && data.length > 0) {
          const item = data[0];
          const resolvedLevel = resolveLevel(item.divvalue);
          setMessage(item.divvaluename);
          setLevel(resolvedLevel);

          const storageKey = `${DISMISS_STORAGE_KEY_PREFIX}${resolvedLevel}:${item.divvaluename}`;
          try {
            // メッセージ内容やレベルが変わるたびに新しいキーが増えて溜まり続けないよう、
            // 現在表示中のメッセージ以外の古い閉じ履歴はここで削除する。
            for (let i = window.localStorage.length - 1; i >= 0; i--) {
              const key = window.localStorage.key(i);
              if (key && key.startsWith(DISMISS_STORAGE_KEY_PREFIX) && key !== storageKey) {
                window.localStorage.removeItem(key);
              }
            }

            const dismissedAt = window.localStorage.getItem(storageKey);
            if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_TTL_MS) {
              setDismissed(true);
            }
          } catch {
            // localStorageが使えない環境では常に表示する
          }
        }
      } else if (response.message !== '対象データが存在しません。') {
        // データ無し（メンテナンスメッセージ未設定）は正常系のためログ出力しない。
        console.error('メンテナンスメッセージ取得エラー:', response);
      }
    } catch (error) {
      console.error('メンテナンスメッセージ取得処理中にエラーが発生しました:', error);
    }
  }, []);

  useEffect(() => {
    loadMaintenanceMessage();
  }, [loadMaintenanceMessage]);

  const handleDismiss = () => {
    setDismissed(true);
    const storageKey = `${DISMISS_STORAGE_KEY_PREFIX}${level}:${message}`;
    try {
      window.localStorage.setItem(storageKey, String(Date.now()));
    } catch {
      // 保存できない場合も閉じる動作自体は継続する
    }
  };

  if (!message || dismissed) return null;

  const style = LEVEL_STYLES[level];

  return (
    <div
      role="alert"
      style={{
        position: 'relative',
        padding: '12px 40px 12px 16px',
        backgroundColor: style.backgroundColor,
        borderTop: `1px solid ${style.borderColor}`,
        borderBottom: `1px solid ${style.borderColor}`,
        textAlign: 'center'
      }}
    >
      <Text style={{ fontSize: '14px', color: style.textColor, whiteSpace: 'pre-line' }}>
        {style.icon}
        {message.replace(/<br>/g, '\n')}
      </Text>
      <button
        type="button"
        onClick={handleDismiss}
        onMouseEnter={() => setHoverClose(true)}
        onMouseLeave={() => setHoverClose(false)}
        aria-label="メンテナンスメッセージを閉じる"
        style={{
          position: 'absolute',
          top: '50%',
          right: '12px',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          color: hoverClose ? style.hoverColor : style.textColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CloseOutlined style={{ fontSize: 14 }} />
      </button>
    </div>
  );
};

export default MaintenanceMessageBanner;
